import React, { createContext, useContext, useState } from 'react';
import hoistNonReactStatics from 'hoist-non-react-statics';
import { useSessionStorage } from '../../hooks/useStorage';
import useHasMounted from '../../hooks/useHasMounted';
import { EstablishmentFormModel, IOfferEstablishementFormModel, OfferFormModel, PaymentFormModel, Yup } from '@model/dist/esm'
import { ReactNode } from 'react';
import { useMemo } from 'react';

interface Value {
    createForm: IOfferEstablishementFormModel,
    createFormValidationSchema: any
    updateCreateForm: ({ offerForm: OfferFormModel,
        establishmentForm: EstablishmentFormModel,
    }) => void,
    createFormComplete: boolean;
    setCreateFormComplete: (value: boolean) => void;
    paymentForm: PaymentFormModel;
    paymentFormValidationSchema: typeof PaymentFormModel.validationSchema;
    updatePaymentForm: (paymentForm: PaymentFormModel) => void
    clearForm: () => void;
}

const Context = createContext<Value | null>(null);

export const useJobOffer = (): Value => {
    const value = useContext(Context)
    if (value === null) throw new Error('no value provided')
    return value;
}

const JobOfferProvider = ({ children }: { children: ReactNode }) => {

    const hasMounted = useHasMounted()

    const [createFormComplete, setCreateFormComplete] = useSessionStorage(
        `jobOfferCreateFormComplete-${OfferFormModel.VERSION}-${EstablishmentFormModel.VERSION}`,
        useState(false)
    );

    const defaultEmptyEnumsValues = {
        employmentType: '',
        contractType: '',
        teachingType: '',
    };
    const [createForm, updateCreateForm] = useSessionStorage(
        `jobOfferCreateForm-${OfferFormModel.VERSION}-${EstablishmentFormModel.VERSION}`,
        useState<Value['createForm']>({
            // #hack on force l'initialisation des valeurs enums pour que Yup les valide
            offerForm: { ...(new OfferFormModel()), ...defaultEmptyEnumsValues } as OfferFormModel,
            establishmentForm: new EstablishmentFormModel()
        })
    )
    const [paymentForm, updatePaymentForm] = useSessionStorage(
        `jobOfferPaymentForm-${PaymentFormModel.VERSION}`,
        useState<Value['paymentForm']>(
            new PaymentFormModel()
        )
    )
    const clearForm = () => {
        updateCreateForm({
            offerForm: new OfferFormModel(),
            establishmentForm: new EstablishmentFormModel(),
        })
        updatePaymentForm(new PaymentFormModel())
        setCreateFormComplete(false)
    }
    const createFormValidationSchema = Yup.object({
        offerForm: OfferFormModel.validationSchema,
        establishmentForm: EstablishmentFormModel.validationSchema
    })
    const paymentFormValidationSchema = PaymentFormModel.validationSchema;



    const value = useMemo(() => ({
        createForm,
        createFormValidationSchema,
        updateCreateForm,
        createFormComplete,
        setCreateFormComplete,
        paymentForm,
        paymentFormValidationSchema,
        updatePaymentForm,
        clearForm,
    }), [createForm, createFormValidationSchema, updateCreateForm, paymentForm, paymentFormValidationSchema, updatePaymentForm, clearForm])


    // Pas de contenu si on est côté serveur
    if (!hasMounted) {
        return null;
    }

    return <Context.Provider value={value}>{children}</Context.Provider>
}

export const withJobOfferContext = (Component) => {
    const wrapper: React.FC<any> = ({ ...props }) => {

        return (
            <JobOfferProvider>
                <Component />
            </JobOfferProvider>
        )
    }

    return hoistNonReactStatics(
        wrapper,
        Component
    );
}
